import { getCookie, setCookie } from 'cookies-next'

export interface AnimatedBackgroundCookieState {
  masterSeed: number
  startTime: number
  lastUpdateTime: number
}

const COOKIE_KEY = 'animatedBackgroundState'
const COOKIE_MAX_AGE = 60 * 60 * 24 * 7 // 7 days

// Get state from cookies
export function getAnimatedBackgroundCookieState(): AnimatedBackgroundCookieState | null {
  try {
    const cookieValue = getCookie(COOKIE_KEY)
    if (!cookieValue || typeof cookieValue !== 'string') {
      return null
    }

    const parsed = JSON.parse(cookieValue) as AnimatedBackgroundCookieState

    // Validate the structure
    if (
      typeof parsed.masterSeed === 'number' &&
      typeof parsed.startTime === 'number' &&
      typeof parsed.lastUpdateTime === 'number'
    ) {
      return parsed
    }

    return null
  } catch (error) {
    console.warn('Failed to parse animated background cookie state:', error)
    // Clear the cookie
    setCookie(COOKIE_KEY, '', {
      maxAge: 0,
      sameSite: 'lax',
      secure: process.env.NODE_ENV === 'production',
    })

    return null
  }
}

// Save state to cookies
export function setAnimatedBackgroundCookieState(state: AnimatedBackgroundCookieState): void {
  try {
    const cookieValue = JSON.stringify(state)
    setCookie(COOKIE_KEY, cookieValue, {
      maxAge: COOKIE_MAX_AGE,
      sameSite: 'lax',
      secure: process.env.NODE_ENV === 'production',
    })
  } catch (error) {
    console.warn('Failed to save animated background cookie state:', error)
  }
}

// Initialize or get existing cookie state
export function initializeAnimatedBackgroundCookieState(): AnimatedBackgroundCookieState {
  const existing = getAnimatedBackgroundCookieState()

  if (existing) {
    // Update last access time
    const updated = {
      ...existing,
      lastUpdateTime: Date.now(),
    }
    setAnimatedBackgroundCookieState(updated)
    return updated
  }

  // Create new state
  const newState: AnimatedBackgroundCookieState = {
    masterSeed: Math.floor(Math.random() * 1000000),
    startTime: Date.now(),
    lastUpdateTime: Date.now(),
  }

  setAnimatedBackgroundCookieState(newState)
  return newState
}

// Calculate elapsed time since started
export function getElapsedTime(cookieState: AnimatedBackgroundCookieState): number {
  return Date.now() - cookieState.startTime
}

// Check if cookie state is still valid (not too old)
export function isCookieStateValid(cookieState: AnimatedBackgroundCookieState): boolean {
  const maxAge = COOKIE_MAX_AGE * 1000 // Convert to ms
  const age = Date.now() - cookieState.lastUpdateTime
  return age < maxAge
}
